import { Check, MessageSquare, Trash2, X } from 'lucide-react';
import { GroupType } from '../../../types/groups';
import type { GroupDto, GroupMemberDto } from '../../../types/groups';
import { useFieldLabel } from '../../../hooks/useFieldLabel';
import PhotoImage from '../../common/PhotoImage';

interface GroupDetailsModalProps {
  group: GroupDto;
  selectedMemberIds: string[];
  canRemoveMembers: boolean;
  currentUserId: string;
  onToggleMember: (userId: string) => void;
  onMessageSelected: () => void;
  onRemoveMember: (member: GroupMemberDto) => void;
  onClose: () => void;
}

function formatCreatedDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function getMemberName(member: GroupMemberDto) {
  return member.user?.name?.trim() || 'Unknown User';
}

function getMemberPhotoId(member: GroupMemberDto) {
  return member.user?.profilePhotoId ?? null;
}

function normalizeUserId(userId: unknown) {
  return String(userId ?? '').trim();
}

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

export default function GroupDetailsModal({
  group,
  selectedMemberIds,
  canRemoveMembers,
  currentUserId,
  onToggleMember,
  onMessageSelected,
  onRemoveMember,
  onClose,
}: GroupDetailsModalProps) {
  const groupName = group.groupName?.trim() || 'Untitled List';
  const description = group.description?.trim() || 'No description available.';
  const createdBy = group.createdByUser?.name?.trim() || 'Unknown User';
  const groupType = group.groupType?.trim() || GroupType.Project;
  const members = group.members ?? [];
  const listLabel = useFieldLabel('MENU_LIST', 'List');
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4 py-6" role="dialog" aria-modal="true">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="mb-0 text-lg font-bold text-gray-900">View Details</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100 hover:text-gray-900"
            aria-label="Close list details"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">List Name</p>
            <p className="mb-0 text-base font-semibold text-gray-900">{groupName}</p>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Description</p>
            <p className="mb-0 text-sm leading-6 text-gray-700">{description}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Created By</p>
              <p className="mb-0 text-sm text-gray-900">{createdBy}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Created Date</p>
              <p className="mb-0 text-sm text-gray-900">{formatCreatedDate(group.createdAt)}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{listLabel} Type</p>
              <p className="mb-0 text-sm text-gray-900">{groupType}</p>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Member Count</p>
              <p className="mb-0 text-sm text-gray-900">{members.length}</p>
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Member List</p>
            <div className="space-y-2">
              {members.length > 0 ? members.map((member) => {
                const memberUserId = normalizeUserId(member.userId);
                const isSelected = selectedMemberIds.includes(memberUserId);
                const isCurrentUser = memberUserId === normalizeUserId(currentUserId);

                return (
                <div
                  key={memberUserId}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-3 transition ${
                    isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                 <div className="flex h-4 w-4 shrink-0 items-center justify-center"> 
                  { !isCurrentUser && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleMember(memberUserId)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label={`Select ${getMemberName(member)}`}
                  />)}
                  </div>
                  {getMemberPhotoId(member) ? (
                    <PhotoImage
                      photoId={getMemberPhotoId(member)}
                      alt={getMemberName(member)}
                      className="h-10 w-10 rounded-full object-cover"
                      fallback={
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                          {getInitials(getMemberName(member)) || <MessageSquare className="h-4 w-4" />}
                        </span>
                      }
                    />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
                      {getInitials(getMemberName(member)) || <MessageSquare className="h-4 w-4" />}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="mb-0 truncate text-sm font-semibold text-gray-900">{getMemberName(member)}</p>
                    <p className="mb-0 truncate text-xs text-gray-500">{member.user?.specialtyName || 'Specialty not available'}</p>
                    {/* <p className="mb-0 truncate text-xs text-gray-400">{member.user?.availabilityStatus || 'Availability not available'}</p> */}
                  </div>
                  {isSelected ? <Check className="h-4 w-4 text-blue-600" /> : null}
                  {canRemoveMembers && !isCurrentUser ? (
                    <button
                      type="button"
                      onClick={() => onRemoveMember(member)}
                      className="inline-flex h-9 items-center justify-center gap-1 rounded-lg px-2 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  ) : null}
                </div>
              );
              }) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
                  No members found
                </div>
              )}
            </div>
          </div>
        </div>
        {selectedMemberIds.length > 0 ? (
          <div className="flex items-center justify-between gap-3 border-t border-blue-100 bg-blue-50 px-5 py-4">
            <p className="mb-0 text-sm font-semibold text-blue-900">
              {selectedMemberIds.length} {selectedMemberIds.length === 1 ? 'Member' : 'Members'} Selected
            </p>
            <button
              type="button"
              onClick={onMessageSelected}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              <MessageSquare className="h-4 w-4" />
              Message Selected
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
